import React from 'react';
import { Award, ArrowUpRight, TrendingUp } from 'lucide-react';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { marketData } from '../../../data/terminalData';

interface CreditMomentumPanelProps {
  isExpanded?: boolean;
}

export const CreditMomentumPanel: React.FC<CreditMomentumPanelProps> = ({ isExpanded = false }) => {
  const { ratings } = marketData;

  const ratingAgencies = [
    {
      name: "S&P",
      rating: ratings.sp.rating,
      outlook: ratings.sp.outlook,
      change: ratings.sp.change,
      date: 'Nov 2025',
    },
    {
      name: "Fitch",
      rating: ratings.fitch.rating,
      outlook: ratings.fitch.outlook,
      change: ratings.fitch.change,
      date: 'Jun 2025',
    },
    {
      name: "Moody's",
      rating: ratings.moodys.rating,
      outlook: ratings.moodys.outlook,
      change: ratings.moodys.change,
      date: 'Jun 2025',
    },
  ];

  // Investment Grade progress scale
  const ratingScale = ['B', 'B+', 'BB-', 'BB', 'BB+', 'BBB-'];
  const currentPosition = 3; // BB is at index 3
  const igPosition = 5; // BBB- is Investment Grade
  const notchesAway = igPosition - currentPosition; // 2 notches away

  return (
    <div className="bg-terminal-panel border border-terminal-border rounded-lg overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="px-5 py-4 border-b border-terminal-border bg-terminal-bg/30 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Award size={18} className="text-data-amber" />
          <h2 className="text-base font-bold text-white uppercase tracking-wide">Credit Momentum</h2>
        </div>
        <span className="text-xs text-data-green font-semibold px-2 py-1 bg-data-green/10 rounded flex items-center gap-1 border border-data-green/20">
          <ArrowUpRight size={12} />
          2× UPGRADED 2025
        </span>
      </div>

      <div className="flex-1 p-5">
        {/* Rating Cards - Horizontal Layout */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {ratingAgencies.map((agency) => (
            <div
              key={agency.name}
              className="bg-terminal-bg/60 rounded-xl p-4 border border-terminal-border/50 text-center"
            >
              {/* Agency Name */}
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">{agency.name}</div>

              {/* Rating - Large */}
              <div className="text-3xl lg:text-4xl font-mono font-black text-white mb-2">
                {agency.rating}
              </div>

              {/* Change Badge */}
              <div className="flex items-center justify-center gap-1">
                <div className="inline-flex items-center gap-1 px-2 py-1 bg-data-green/10 rounded-full border border-data-green/20">
                  <ArrowUpRight size={12} className="text-data-green" />
                  <span className="text-xs font-semibold text-data-green">{agency.change}</span>
                </div>
              </div>

              {/* Outlook */}
              <div className="mt-2 text-xs text-gray-500">{agency.outlook}</div>
            </div>
          ))}
        </div>

        {/* Progress to Investment Grade */}
        <div className="bg-terminal-bg/40 rounded-lg p-4 border border-terminal-border/30">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-gray-400 uppercase tracking-wider">Path to Investment Grade</span>
            <span className="text-xs text-data-amber font-semibold">{notchesAway} notches away</span>
          </div>

          {/* Progress Scale */}
          <div className="relative">
            {/* Track */}
            <div className="h-3 bg-terminal-border rounded-full overflow-hidden">
              {/* Progress fill */}
              <div
                className="h-full bg-gradient-to-r from-data-green/50 to-data-green rounded-full"
                style={{ width: `${(currentPosition / (ratingScale.length - 1)) * 100}%` }}
              />
            </div>

            {/* Scale Labels */}
            <div className="flex justify-between mt-2">
              {ratingScale.map((rating, idx) => (
                <div key={rating} className="flex flex-col items-center">
                  {/* Marker */}
                  <div className={`w-2 h-2 rounded-full mb-1 ${
                    idx === currentPosition
                      ? 'bg-data-green ring-2 ring-data-green/30'
                      : idx === igPosition
                        ? 'bg-data-amber'
                        : 'bg-terminal-border'
                  }`} />
                  <span className={`text-xs font-mono ${
                    idx === currentPosition
                      ? 'text-data-green font-bold'
                      : idx === igPosition
                        ? 'text-data-amber font-bold'
                        : 'text-gray-600'
                  }`}>
                    {rating}
                  </span>
                  {idx === igPosition && (
                    <span className="text-xs text-data-amber">(IG)</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* IG Projection */}
          <div className="mt-4 flex items-center justify-center gap-2 py-2 bg-data-amber/10 rounded-lg border border-data-amber/20">
            <TrendingUp size={14} className="text-data-amber" />
            <span className="text-xs text-data-amber">
              <span className="font-bold">Investment Grade</span> trajectory expected by 2027
            </span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-terminal-border/50 flex items-center justify-between">
        <DataSourceBadge source={DATA_SOURCES.SP} showLabel size="xs" />
        <span className="text-xs text-gray-500">Dec 2025</span>
      </div>
    </div>
  );
};

export default CreditMomentumPanel;
